import React, { useState, useEffect, useCallback } from 'react'
import { Users, UserPlus, TrendingUp, Activity, GitMerge, MapPin, User2 } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  BarChart, Bar,
} from 'recharts'
import { fetchAnalytics } from '../../../../api/analyticsClient'
import KPICard from '../components/KPICard'
import AnalyticsCard from '../components/AnalyticsCard'
import styles from './GrowthSection.module.css'

const COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2']

const fmt = (n) => {
  if (n === null || n === undefined) return '—'
  const num = Number(n)
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
  return num.toLocaleString('en-IN')
}
const fmtPct = (n) => (n !== null && n !== undefined) ? parseFloat(n).toFixed(1) + '%' : '—'

const CHANNEL_LABELS = { MOBILE_APP: 'Mobile App', ADMIN: 'Admin', UNTRACKED: 'Untracked (pre-instrumentation)' }

const initState = () => ({ data: null, loading: true, error: null })

const GrowthSection = ({ dateRange }) => {
  const [summary, setSummary] = useState(initState)
  const [trend, setTrend] = useState(initState)
  const [channels, setChannels] = useState(initState)
  const [referral, setReferral] = useState(initState)
  const [geo, setGeo] = useState(initState)
  const [gender, setGender] = useState(initState)
  const [granularity, setGranularity] = useState('daily')
  const [geoLevel, setGeoLevel] = useState('city')

  const { from, to } = dateRange

  const loadAll = useCallback(async () => {
    setSummary(s => ({ ...s, loading: true, error: null }))
    setTrend(s => ({ ...s, loading: true, error: null }))
    setChannels(s => ({ ...s, loading: true, error: null }))
    setReferral(s => ({ ...s, loading: true, error: null }))
    setGeo(s => ({ ...s, loading: true, error: null }))
    setGender(s => ({ ...s, loading: true, error: null }))

    const [summaryRes, trendRes, channelsRes, referralRes, geoRes, genderRes] = await Promise.all([
      fetchAnalytics('/analytics/growth/summary', { from, to }),
      fetchAnalytics('/analytics/growth/trend', { from, to, granularity }),
      fetchAnalytics('/analytics/growth/acquisition-channels', { from, to }),
      fetchAnalytics('/analytics/growth/referral', { from, to }),
      fetchAnalytics('/analytics/growth/geo', { from, to, level: geoLevel, limit: 10 }),
      fetchAnalytics('/analytics/growth/gender', { from, to }),
    ])

    setSummary({ data: summaryRes.data, loading: false, error: summaryRes.error })
    setTrend({ data: trendRes.data, loading: false, error: trendRes.error })
    setChannels({ data: channelsRes.data, loading: false, error: channelsRes.error })
    setReferral({ data: referralRes.data, loading: false, error: referralRes.error })
    setGeo({ data: geoRes.data, loading: false, error: geoRes.error })
    setGender({ data: genderRes.data, loading: false, error: genderRes.error })
  }, [from, to, granularity, geoLevel])

  useEffect(() => { loadAll() }, [loadAll])

  const handleGranularity = async (g) => {
    setGranularity(g)
    setTrend(s => ({ ...s, loading: true, error: null }))
    const res = await fetchAnalytics('/analytics/growth/trend', { from, to, granularity: g })
    setTrend({ data: res.data, loading: false, error: res.error })
  }

  const handleGeoLevel = async (level) => {
    setGeoLevel(level)
    setGeo(s => ({ ...s, loading: true, error: null }))
    const res = await fetchAnalytics('/analytics/growth/geo', { from, to, level, limit: 10 })
    setGeo({ data: res.data, loading: false, error: res.error })
  }

  const referralEpoch = new Date('2026-06-01')
  const toDate = new Date(to)
  const showReferral = toDate >= referralEpoch

  const growthStatus = summary.data?.growth_pct > 0 ? 'green' : summary.data?.growth_pct < 0 ? 'red' : undefined

  return (
    <div className={styles.section}>
      {/* KPI Row */}
      <div className={styles.kpiRow}>
        <KPICard label="Total Registered" value={fmt(summary.data?.total_registered)} icon={Users} loading={summary.loading} />
        <KPICard
          label="New in Period"
          value={fmt(summary.data?.new_in_period)}
          icon={UserPlus}
          trend={summary.data?.growth_pct}
          trendLabel="vs prev period"
          loading={summary.loading}
        />
        <KPICard label="Previous Period" value={fmt(summary.data?.previous_period)} icon={TrendingUp} loading={summary.loading} />
        <KPICard
          label="Growth"
          value={fmtPct(summary.data?.growth_pct)}
          icon={TrendingUp}
          status={growthStatus}
          loading={summary.loading}
        />
        <KPICard label="Active Last 30d" value={fmt(summary.data?.active_last_30d)} icon={Activity} loading={summary.loading} />
      </div>

      {/* Registration Trend */}
      <AnalyticsCard
        title="Registration Trend"
        subtitle="Daily new stylist registrations"
        loading={false}
        error={trend.error}
        actions={
          <div className={styles.toggle}>
            {['daily', 'weekly', 'monthly'].map(g => (
              <button
                key={g}
                className={`${styles.toggleBtn} ${granularity === g ? styles.toggleBtnActive : ''}`}
                onClick={() => handleGranularity(g)}
              >
                {g[0].toUpperCase() + g.slice(1)}
              </button>
            ))}
          </div>
        }
      >
        {trend.loading ? (
          <div className={styles.chartSkeleton} />
        ) : !trend.data?.length ? (
          <div className={styles.empty}>No data for this period</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend.data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
              <Tooltip
                contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                formatter={v => [fmt(v), 'Registrations']}
              />
              <Line type="monotone" dataKey="registrations" stroke="#2563eb" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#2563eb' }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </AnalyticsCard>

      {/* Acquisition + Geo */}
      <div className={styles.chartGrid}>
        <AnalyticsCard
          title="Acquisition Channels"
          subtitle="Registration source breakdown"
          loading={channels.loading}
          error={channels.error}
        >
          {channels.data?.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={channels.data} dataKey="count" nameKey="channel" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2}>
                    {channels.data.map((entry, i) => (
                      <Cell key={entry.channel} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v, name) => [fmt(v), CHANNEL_LABELS[name] || name]}
                    contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.legendList}>
                {channels.data.map((ch, i) => (
                  <div key={ch.channel} className={styles.legendRow}>
                    <span className={styles.legendDot} style={{ background: COLORS[i % COLORS.length] }} />
                    <span className={styles.legendName}>{CHANNEL_LABELS[ch.channel] || ch.channel}</span>
                    <span className={styles.legendValue}>{fmt(ch.count)}</span>
                    <span className={styles.legendPct}>{fmtPct(ch.pct)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title="Geographic Distribution"
          subtitle="Top locations by registrations"
          loading={geo.loading}
          error={geo.error}
          actions={
            <div className={styles.toggle}>
              {['city', 'state'].map(level => (
                <button
                  key={level}
                  className={`${styles.toggleBtn} ${geoLevel === level ? styles.toggleBtnActive : ''}`}
                  onClick={() => handleGeoLevel(level)}
                >
                  {level[0].toUpperCase() + level.slice(1)}
                </button>
              ))}
            </div>
          }
        >
          {geo.data?.length > 0 && (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={geo.data} layout="vertical" margin={{ top: 0, right: 16, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} tickFormatter={fmt} />
                <YAxis type="category" dataKey="location" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={60} />
                <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} formatter={v => [fmt(v), 'Registrations']} />
                <Bar dataKey="registrations" fill="#2563eb" radius={[0, 4, 4, 0]} maxBarSize={16} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </AnalyticsCard>
      </div>

      {/* Gender + Referral */}
      <div className={styles.chartGrid}>
        <AnalyticsCard
          title="Gender Split"
          subtitle="Stylist gender distribution"
          loading={gender.loading}
          error={gender.error}
        >
          {gender.data?.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={gender.data} dataKey="count" nameKey="gender" cx="50%" cy="50%" innerRadius={48} outerRadius={76} paddingAngle={2}>
                    {gender.data.map((entry, i) => (
                      <Cell key={entry.gender} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={v => [fmt(v), 'Count']} contentStyle={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className={styles.legendList}>
                {gender.data.map((g, i) => (
                  <div key={g.gender} className={styles.legendRow}>
                    <span className={styles.legendDot} style={{ background: COLORS[i % COLORS.length] }} />
                    <span className={styles.legendName}>{g.gender}</span>
                    <span className={styles.legendValue}>{fmt(g.count)}</span>
                    <span className={styles.legendPct}>{fmtPct(g.pct)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </AnalyticsCard>

        {showReferral ? (
          <AnalyticsCard
            title="Referral Program"
            subtitle="Referral-driven registrations"
            loading={referral.loading}
            error={referral.error}
          >
            {referral.data && (
              <div className={styles.referralContent}>
                {referral.data.note && (
                  <div className={styles.referralNote}>
                    <GitMerge size={13} />
                    Referral program launched Jun 1, 2026 — data is from {referral.data.effective_from}
                  </div>
                )}
                <div className={styles.referralKpis}>
                  <div className={styles.referralKpi}>
                    <span className={styles.referralKpiValue}>{fmt(referral.data.referred)}</span>
                    <span className={styles.referralKpiLabel}>Referred</span>
                  </div>
                  <div className={styles.referralDivider} />
                  <div className={styles.referralKpi}>
                    <span className={styles.referralKpiValue}>{fmt(referral.data.non_referred)}</span>
                    <span className={styles.referralKpiLabel}>Non-Referred</span>
                  </div>
                  <div className={styles.referralDivider} />
                  <div className={styles.referralKpi}>
                    <span className={`${styles.referralKpiValue} ${styles.referralHighlight}`}>
                      {fmtPct(referral.data.referral_rate_pct)}
                    </span>
                    <span className={styles.referralKpiLabel}>Referral Rate</span>
                  </div>
                </div>
                <div className={styles.referralBarWrap}>
                  <div className={styles.referralBarTrack}>
                    <div
                      className={styles.referralBarFill}
                      style={{ width: `${Math.min(100, referral.data.referral_rate_pct || 0)}%` }}
                    />
                  </div>
                  <span className={styles.referralBarLabel}>{fmtPct(referral.data.referral_rate_pct)} of registrations via referral</span>
                </div>
              </div>
            )}
          </AnalyticsCard>
        ) : (
          <AnalyticsCard title="Referral Program" subtitle="Not yet active for selected range" loading={false} error={null}>
            <div className={styles.empty}>Referral program launched Jun 1, 2026. Select a date range on or after that date.</div>
          </AnalyticsCard>
        )}
      </div>
    </div>
  )
}

export default GrowthSection
